import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dbb2q5uctmbk4sp5can0';
const SESSION_KEY = 'lpp_consult_popup_seen';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ConsultationPopup() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      seen = false;
    }
    if (seen) return;

    const timer = window.setTimeout(() => {
      setOpen(true);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePopup();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const closePopup = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* ignore storage errors */
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setFormError('');
      setStatus('success');
      form.reset();
      return;
    }

    if (!formData.get('consent')) {
      setStatus('error');
      setFormError('Please tick the consent box so we know we may contact you.');
      return;
    }

    formData.delete('website_alt');
    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText ||
        '';

      const codeOk =
        response.ok &&
        (typeof parsed?.code !== 'string' || parsed.code.toUpperCase() === 'OK');
      const spamHit = typeof serverMessage === 'string' && serverMessage.toLowerCase().includes('spam');

      if (!codeOk || spamHit) {
        setStatus('error');
        setFormError(
          typeof serverMessage === 'string' && serverMessage.trim().length > 0
            ? serverMessage
            : 'Something went wrong while sending your request. Please try again.',
        );
        return;
      }

      form.reset();
      setStatus('success');
    } catch {
      setStatus('error');
      setFormError('We could not reach the server. Please check your connection and try again.');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 md:px-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consult-popup-title"
    >
      <button
        type="button"
        aria-label="Close consultation popup"
        onClick={closePopup}
        className="absolute inset-0 w-full h-full bg-navy-950/60 backdrop-blur-sm cursor-pointer animate-fade-in"
      />

      <div className="consult-popup-card relative w-full max-w-[980px] max-h-[92vh] overflow-y-auto bg-white rounded-lg animate-scale-in">
        <button
          type="button"
          onClick={closePopup}
          aria-label="Close"
          className="absolute top-3 right-3 md:top-5 md:right-5 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 text-navy-900 hover:bg-cream-100 transition-colors duration-300 cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Image panel */}
          <div className="relative hidden md:block min-h-[560px] bg-cream-100">
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20lifestyle%20photograph%20of%20a%20shiny%20gold%20copper%20coffee%20cup%20on%20a%20matching%20ornate%20saucer%20resting%20on%20a%20white%20painted%20wooden%20table%2C%20behind%20it%20a%20black%20metal%20wire%20basket%20overflowing%20with%20stacked%20books%20and%20folded%20magazines%2C%20a%20small%20white%20ceramic%20pot%20with%20delicate%20green%20herb%20sprigs%20at%20the%20left%2C%20bright%20soft%20morning%20window%20light%2C%20pale%20white%20background%20with%20blurred%20white%20curtains%2C%20shallow%20depth%20of%20field%2C%20warm%20serene%20editorial%20styling&width=800&height=1100&seq=consult-popup-books-2026&orientation=portrait&nocache=true"
              alt="Books and a warm cup of coffee on a bright desk"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>

          {/* Form panel */}
          <div className="p-6 md:p-9 lg:p-10">
            {status === 'success' ? (
              <div className="flex flex-col items-start py-6 animate-scale-in">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-navy-500 mb-5">
                  <i className="ri-check-line text-white text-xl" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-navy-900 mb-3">
                  Thank you — we got it!
                </h2>
                <p className="text-sm text-navy-700 leading-relaxed mb-6">
                  One of our publishing consultants will reach out shortly to set up your free
                  consultation.
                </p>
                <button
                  type="button"
                  onClick={closePopup}
                  className="px-6 py-3 text-sm font-medium text-white bg-navy-900 rounded-md hover:bg-navy-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Back to the site
                </button>
              </div>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-[0.18em] text-gold-600 mb-3">
                  Free consultation
                </p>
                <h2
                  id="consult-popup-title"
                  className="text-2xl md:text-[28px] leading-tight font-bold text-navy-900 mb-3"
                >
                  We&apos;re here to help you!
                </h2>
                <p className="text-sm text-navy-700 leading-relaxed mb-6">
                  Need help publishing or marketing your book? Have a quick FREE consultation with
                  the professionals.
                </p>

                <form data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                  <div className="min-w-0">
                    <label htmlFor="consult-name" className="sr-only">
                      Name
                    </label>
                    <input
                      id="consult-name"
                      name="name"
                      type="text"
                      required
                      placeholder="Name"
                      autoComplete="name"
                      className="w-full box-border px-4 py-3.5 text-sm text-navy-900 placeholder:text-navy-400 bg-white border border-cream-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 transition-all duration-300"
                    />
                  </div>

                  <div className="min-w-0">
                    <label htmlFor="consult-email" className="sr-only">
                      Email Address
                    </label>
                    <input
                      id="consult-email"
                      name="email"
                      type="email"
                      required
                      placeholder="Email Address"
                      autoComplete="email"
                      className="w-full box-border px-4 py-3.5 text-sm text-navy-900 placeholder:text-navy-400 bg-white border border-cream-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 transition-all duration-300"
                    />
                  </div>

                  <div className="min-w-0">
                    <label htmlFor="consult-phone" className="sr-only">
                      Phone Number
                    </label>
                    <input
                      id="consult-phone"
                      name="phone"
                      type="tel"
                      placeholder="Phone Number"
                      autoComplete="tel"
                      className="w-full box-border px-4 py-3.5 text-sm text-navy-900 placeholder:text-navy-400 bg-white border border-cream-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 transition-all duration-300"
                    />
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="consult-extra-field"
                  />

                  <label
                    htmlFor="consult-consent"
                    className="flex items-start gap-3 text-sm text-navy-700 leading-relaxed cursor-pointer pt-1"
                  >
                    <input
                      id="consult-consent"
                      name="consent"
                      type="checkbox"
                      value="yes"
                      className="mt-0.5 w-4 h-4 shrink-0 accent-navy-900 cursor-pointer"
                    />
                    <span>
                      I agree to the{' '}
                      <Link
                        to="/privacy-policy"
                        onClick={closePopup}
                        className="text-navy-500 underline underline-offset-2 hover:text-navy-600"
                      >
                        Privacy Policy
                      </Link>{' '}
                      and consent to Leavitt Peak Press contacting me about my enquiry.
                    </span>
                  </label>

                  {status === 'error' && formError && (
                    <p className="text-xs text-red-600 leading-relaxed" role="alert">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full box-border mt-2 px-6 py-4 text-sm font-medium tracking-wide text-white bg-navy-900 rounded-md hover:bg-navy-800 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? 'SENDING...' : 'SUBMIT'}
                  </button>

                  <p className="text-[11px] text-navy-400 leading-relaxed pt-1">
                    No obligation. We reply within one business day.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}